import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiV1 } from "../Auth/Interceptor";

const initialStateValue = {
    activeSection: 'feed',
    activeProfileTab: 'posts',
    modal: {
        activeModal: '',
        displayModal: false,
        selectedPost: {},
        selectedComment: {}
    },
    viewerKebab: {
        kebabOpen: false,
        postID: null,
    },
    media: {
        images: [],
        video: null,
    },
    carouselMenu: 'preview',
    video: {
        menu: 'edit',
        editSection: null,
        text: {
            content: null,
            align: 'left',
            size: 12,
            color: null,
        }
    },
    tagList: {
        isVisible: false,
        product: {
            name: null,
            id: null,
        },
    },
    storeData: {
        storeData: {},
        loading: 'idle',
        error: null,
    },
    feedPosts: {
        feedPosts: [],
        loading: 'idle',
        error: null,
    },
    sellerInfo: {
        sellerInfo: {},
        loading: 'idle',
        error: null,
    },
    sellerReviews: {
        sellerReviews: [],
        loading: 'idle',
        error: null,
    },
    commentDeleted: false,
    childCommentDeleted: false,
}

const fetchSellerReviews = createAsyncThunk('lens/fetchSellerReviews', async () => {
    try {
        const response = await apiV1.get(`sellerProfile/getAllReview`);
        return response.data.data;
    }
    catch(err) {
        throw err;
    }
});

const fetchFeedPosts = createAsyncThunk('lens/fetchFeedPosts', async () => {
    try {
        const response = await apiV1.get(`sellerpost/getAllPost`);
        return response.data.data;
    }
    catch(error) {
        throw error;
    }
});

const fetchSellerInfo = createAsyncThunk('lens/fetchSellerInfo', async (UserId) => {
    try {
        const response = await apiV1.get(`sellerProfile/profile/${UserId}`);
        return { ...response.data.data, id: UserId };
    }
    catch(err) {
        throw err;
    }
});

const fetchStoreData = createAsyncThunk('lens/fetchStoreData', async () => {
    try {
        const response = await apiV1.get(`settings/store`);
        return response.data.data.seller;
    }
    catch(error) {
        throw error;
    }
});

const lensSlice = createSlice({
    name: 'lens',
    initialState: initialStateValue,
    reducers: {
        changeSection: (state, action) => {
            state.activeSection = action.payload;
        },
        changeProfileTab: (state, action) => {
            state.activeProfileTab = action.payload;
        },
        setModal: (state, action) => {
            state.modal.activeModal = action.payload;
            state.modal.displayModal = true;
        },
        closeModal: (state, _) => {
            state.modal.activeModal = '';
            state.modal.displayModal = false;
        },
        openViewerKebabMenu: (state, action) => {
            state.viewerKebab.kebabOpen = true;
            state.viewerKebab.postID = action.payload;
        },
        closeViewerKebabMenu: (state, _) => {
            state.viewerKebab = initialStateValue.viewerKebab;
        },
        setImages: (state, action) => {
            state.media.images = action.payload;
        },
        clearImages: (state, _) => {
            state.media.images = [];
        },
        setCarouselMenu: (state, action) => {
            state.carouselMenu = action.payload;
        },
        setVideo: (state, action) => {
            state.media.video = action.payload;
        },
        setEditSection: (state, action) => {
            state.video.editSection = action.payload;
        },
        setVideoPostMenu: (state, action) => {
            state.video.menu = action.payload;
        },
        toggleTagList: (state, _) => {
            state.tagList.isVisible = !state.tagList.isVisible;
        },
        closeTagList: (state, _) => {
            state.tagList.isVisible = false;
        },
        selectTagListProduct: (state, action) => {
            if(state.tagList.product.id === action.payload.id) {
                state.tagList.product = initialStateValue.tagList.product;
            }
            else {
                state.tagList.product = action.payload;
            }
        },
        setSelectedPost: (state, action) => {
            state.modal.selectedPost = action.payload;
        },
        setSelectedComment: (state, action) => {
            state.modal.selectedComment = action.payload;
        },
        toggleCommentDeleted: (state, _) => {
            state.commentDeleted = !state.commentDeleted;
        },
        toggleChildCommentDeleted: (state, _) => {
            state.childCommentDeleted = !state.childCommentDeleted;
        },
        pushToFeedPosts: (state, action) => {
            state.feedPosts.feedPosts.push(action.payload);
        },
        updateFeedPosts: (state, action) => {
            state.feedPosts.feedPosts = state.feedPosts.feedPosts.filter(post => post.id !== action.payload);
        },
        updateSellerPosts: (state, action) => {
            state.sellerInfo.sellerInfo.posts = state.sellerInfo.sellerInfo.posts.filter(post => post.id !== action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(fetchFeedPosts.pending, (state) => {
            state.feedPosts.loading = 'loading';
        })
        .addCase(fetchFeedPosts.fulfilled, (state, action) => {
            state.feedPosts.loading = 'succeeded';
            state.feedPosts.feedPosts = action.payload;
        })
        .addCase(fetchFeedPosts.rejected, (state, action) => {
            state.feedPosts.loading = 'failed';
            state.feedPosts.error = action.error.message;
        })
        .addCase(fetchStoreData.pending, (state) => {
            state.storeData.loading = 'loading';
        })
        .addCase(fetchStoreData.fulfilled, (state, action) => {
            state.storeData.loading = 'succeeded';
            state.storeData.storeData = action.payload;
        })
        .addCase(fetchStoreData.rejected, (state, action) => {
            state.storeData.loading = 'failed';
            state.storeData.error = action.error.message;
        })
        .addCase(fetchSellerInfo.pending, (state) => {
            state.sellerInfo.loading = 'loading';
        })
        .addCase(fetchSellerInfo.fulfilled, (state, action) => {
            state.sellerInfo.loading = 'succeeded';
            state.sellerInfo.sellerInfo = action.payload;
        })
        .addCase(fetchSellerInfo.rejected, (state, action) => {
            state.sellerInfo.loading = 'failed';
            state.sellerInfo.error = action.error.message;
        })
        .addCase(fetchSellerReviews.pending, (state) => {
            state.sellerReviews.loading = 'loading';
        })
        .addCase(fetchSellerReviews.fulfilled, (state, action) => {
            state.sellerReviews.loading = 'succeeded';
            state.sellerReviews.sellerReviews = action.payload;
        })
        .addCase(fetchSellerReviews.rejected, (state, action) => {
            state.sellerReviews.loading = 'failed';
            state.sellerReviews.error = action.error.message;
        });
    },
});

export const {
    changeSection,
    changeProfileTab,
    setModal,
    closeModal,
    setImages,
    clearImages,
    setCarouselMenu,
    setVideo,
    setEditSection,
    setVideoPostMenu,
    toggleTagList,
    closeTagList,
    selectTagListProduct,
    setSelectedPost,
    setSelectedComment,
    closeViewerKebabMenu,
    openViewerKebabMenu,
    toggleCommentDeleted,
    toggleChildCommentDeleted,
    pushToFeedPosts,
    updateFeedPosts,
    updateSellerPosts,
} = lensSlice.actions;

export {
    fetchFeedPosts,
    fetchStoreData,
    fetchSellerInfo,
    fetchSellerReviews,
}

export default lensSlice.reducer;
